import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axiosInstance from '../../api/axiosInstance';

// Async thunks for API calls
export const fetchReceptionistStats = createAsyncThunk(
  'receptionist/fetchStats',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get('/appointments/receptionist/dashboard/stats/');
      return response.data.stats || {};
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to fetch dashboard stats');
    }
  }
);

export const fetchTodayAppointments = createAsyncThunk(
  'receptionist/fetchTodayAppointments',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get('/appointments/receptionist/appointments/today/');
      return response.data.appointments || [];
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to fetch today\'s appointments');
    }
  }
);

export const updateAppointmentStatus = createAsyncThunk(
  'receptionist/updateAppointmentStatus',
  async ({ appointmentId, status }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.patch(
        `/appointments/receptionist/appointments/${appointmentId}/status/`,
        { status }
      );
      return { appointmentId, ...response.data };
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to update appointment status');
    }
  }
);

export const quickCreatePatient = createAsyncThunk(
  'receptionist/quickCreatePatient',
  async (patientData, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(
        '/appointments/receptionist/patients/quick-create/',
        patientData
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to create patient');
    }
  }
);

export const fetchPatients = createAsyncThunk(
  'receptionist/fetchPatients',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get('/appointments/receptionist/patients/');
      return response.data.patients || [];
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to fetch patients');
    }
  }
);

export const searchPatients = createAsyncThunk(
  'receptionist/searchPatients',
  async (query, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(`/reception/checkin/search/?q=${encodeURIComponent(query)}`);
      return response.data.results || [];
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to search patients');
    }
  }
);

export const fetchClinicInfo = createAsyncThunk(
  'receptionist/fetchClinicInfo',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get('/clinics/receptionist/info/');
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to fetch clinic info');
    }
  }
);

export const fetchBillingHistory = createAsyncThunk(
  'receptionist/fetchBillingHistory',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get('/appointments/receptionist/billing/history/');
      return response.data.payments || [];
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to fetch billing history');
    }
  }
);

export const generateInvoice = createAsyncThunk(
  'receptionist/generateInvoice',
  async (appointmentId, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(
        `/appointments/receptionist/invoices/${appointmentId}/generate/`
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to generate invoice');
    }
  }
);

export const markInvoicePaid = createAsyncThunk(
  'receptionist/markInvoicePaid',
  async (invoiceId, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.patch(
        `/appointments/receptionist/invoices/${invoiceId}/pay/`
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to mark invoice as paid');
    }
  }
);

export const sendMagicLink = createAsyncThunk(
  'receptionist/sendMagicLink',
  async ({ patientId, channel, includeIntake }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(
        `/reception/checkin/${patientId}/send-link/`,
        { channel, include_intake: includeIntake }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to send magic link');
    }
  }
);

const initialState = {
  // Dashboard stats
  stats: {
    total_appointments: 0,
    today_appointments: 0,
    pending_appointments: 0,
    completed_appointments: 0,
    new_patients: 0,
    total_revenue: 0,
  },
  statsLoading: false,
  statsError: null,

  // Today's appointments
  todayAppointments: [],
  appointmentsLoading: false,
  appointmentsError: null,

  // Patients
  patients: [],
  patientsLoading: false,
  patientsError: null,

  // Search results
  searchResults: [],
  searchLoading: false,
  searchError: null,

  // Clinic info
  clinicInfo: null,
  clinicInfoLoading: false,
  clinicInfoError: null,

  // Billing
  billingHistory: [],
  billingLoading: false,
  billingError: null,

  // Last updated timestamp
  lastUpdated: null,
};

const receptionistSlice = createSlice({
  name: 'receptionist',
  initialState,
  reducers: {
    clearSearchResults: (state) => {
      state.searchResults = [];
      state.searchError = null;
    },
    resetErrors: (state) => {
      state.statsError = null;
      state.appointmentsError = null;
      state.patientsError = null;
      state.searchError = null;
      state.clinicInfoError = null;
      state.billingError = null;
    },
  },
  extraReducers: (builder) => {
    // Fetch Stats
    builder
      .addCase(fetchReceptionistStats.pending, (state) => {
        state.statsLoading = true;
        state.statsError = null;
      })
      .addCase(fetchReceptionistStats.fulfilled, (state, action) => {
        state.statsLoading = false;
        state.stats = action.payload;
        state.lastUpdated = new Date().toISOString();
      })
      .addCase(fetchReceptionistStats.rejected, (state, action) => {
        state.statsLoading = false;
        state.statsError = action.payload;
      });

    // Fetch Today Appointments
    builder
      .addCase(fetchTodayAppointments.pending, (state) => {
        state.appointmentsLoading = true;
        state.appointmentsError = null;
      })
      .addCase(fetchTodayAppointments.fulfilled, (state, action) => {
        state.appointmentsLoading = false;
        state.todayAppointments = action.payload;
      })
      .addCase(fetchTodayAppointments.rejected, (state, action) => {
        state.appointmentsLoading = false;
        state.appointmentsError = action.payload;
      });

    // Update Appointment Status
    builder
      .addCase(updateAppointmentStatus.fulfilled, (state, action) => {
        const { appointmentId, new_status } = action.payload;
        const appointment = state.todayAppointments.find(apt => apt.id === appointmentId);
        if (appointment) {
          appointment.status = new_status;
        }
      });

    // Fetch Patients
    builder
      .addCase(fetchPatients.pending, (state) => {
        state.patientsLoading = true;
        state.patientsError = null;
      })
      .addCase(fetchPatients.fulfilled, (state, action) => {
        state.patientsLoading = false;
        state.patients = action.payload;
      })
      .addCase(fetchPatients.rejected, (state, action) => {
        state.patientsLoading = false;
        state.patientsError = action.payload;
      });

    // Search Patients
    builder
      .addCase(searchPatients.pending, (state) => {
        state.searchLoading = true;
        state.searchError = null;
      })
      .addCase(searchPatients.fulfilled, (state, action) => {
        state.searchLoading = false;
        state.searchResults = action.payload;
      })
      .addCase(searchPatients.rejected, (state, action) => {
        state.searchLoading = false;
        state.searchError = action.payload;
      });

    // Fetch Clinic Info
    builder
      .addCase(fetchClinicInfo.pending, (state) => {
        state.clinicInfoLoading = true;
        state.clinicInfoError = null;
      })
      .addCase(fetchClinicInfo.fulfilled, (state, action) => {
        state.clinicInfoLoading = false;
        state.clinicInfo = action.payload;
      })
      .addCase(fetchClinicInfo.rejected, (state, action) => {
        state.clinicInfoLoading = false;
        state.clinicInfoError = action.payload;
      });

    // Fetch Billing History
    builder
      .addCase(fetchBillingHistory.pending, (state) => {
        state.billingLoading = true;
        state.billingError = null;
      })
      .addCase(fetchBillingHistory.fulfilled, (state, action) => {
        state.billingLoading = false;
        state.billingHistory = action.payload;
      })
      .addCase(fetchBillingHistory.rejected, (state, action) => {
        state.billingLoading = false;
        state.billingError = action.payload;
      });
  },
});

export const { clearSearchResults, resetErrors } = receptionistSlice.actions;
export default receptionistSlice.reducer;

